import React, { useMemo } from "react";
import {
  REFLECTION_EMOTION_ORDER,
  REFLECTION_EMOTION_LABELS,
  REFLECTION_EMOTION_COLORS,
  orderedEmotionsWithData
} from "../../constants/emotions.js";
import { getEmotionIconSrc } from "../../constants/mediaAssets.js";
import MediaIcon from "../MediaIcon.js";
import { getSentimentEntries } from "../../utils/dashboardInsights.js";

const EmotionDistributionSection = ({ insights, className = "" }) => {
  const withSentiment = useMemo(() => getSentimentEntries(insights), [insights]);
  const distribution = useMemo(
    () =>
      REFLECTION_EMOTION_ORDER.reduce((acc, key) => {
        acc[key] = withSentiment.filter((i) => i.sentiment === key).length;
        return acc;
      }, {}),
    [withSentiment]
  );
  const total = withSentiment.length;
  const emotionPieKeys = orderedEmotionsWithData(distribution);

  if (total === 0) {
    return (
      <section className={`dashboard-section dashboard-moods mood-distribution ${className}`.trim()} aria-label="Emotional Spread">
        <h2>Emotional Spread</h2>
        <p className="dashboard-empty">No confirmed emotion labels yet. Confirm an emotion in Quick Clarity, or add reflections to see the spread.</p>
      </section>
    );
  }

  return (
    <section className={`dashboard-section dashboard-moods mood-distribution ${className}`.trim()} aria-label="Emotional Spread">
      <h2>Emotional Spread</h2>
      <p className="mood-distribution__total">
        {total} reflection{total !== 1 ? "s" : ""}
      </p>
      <div className="mood-distribution__pie-wrap">
        <div
          className="mood-pie"
          role="img"
          aria-label={`Emotional Spread: ${emotionPieKeys.map((k) => `${REFLECTION_EMOTION_LABELS[k] || k} ${distribution[k] || 0}`).join(", ")}`}
          style={{
            background: (() => {
              let cumulative = 0;
              const parts = emotionPieKeys.map((key) => {
                const count = distribution[key] || 0;
                const pct = (count / total) * 100;
                const start = cumulative;
                cumulative += pct;
                return `${REFLECTION_EMOTION_COLORS[key]} ${start}% ${cumulative}%`;
              });
              return parts.length ? `conic-gradient(${parts.join(", ")})` : "rgba(2, 168, 174, 0.2)";
            })()
          }}
        />
      </div>
      <div className="mood-pie-legend">
        {emotionPieKeys.map((key) => {
          const count = distribution[key] || 0;
          const pct = total ? (count / total) * 100 : 0;
          return (
            <div key={key} className="mood-pie-legend__item">
              <MediaIcon src={getEmotionIconSrc(key)} className="media-icon mood-pie-legend__icon" size={22} alt="" />
              <span className="mood-pie-legend__swatch" style={{ background: REFLECTION_EMOTION_COLORS[key] }} />
              <span className="mood-pie-legend__label">{REFLECTION_EMOTION_LABELS[key] || key}</span>
              <span className="mood-pie-legend__count">
                {count} ({pct.toFixed(0)}%)
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default EmotionDistributionSection;
